import React, { useEffect, useRef } from "react";
import {
  debounceTime,
  takeUntil,
  map,
  distinctUntilChanged,
  tap,
  filter,
  BehaviorSubject,
  merge,
} from "rxjs";
import { select, zoom as d3Zoom } from "d3";
import xstream from "state-x-stream";
import propTypes from "prop-types";
import ZoomService from "../../services/zoomService";
import clamp from "../../../../common/helpers/clamp";

function controller({ props$, destroyed$ }) {
  const { zoomService } = props$.value;
  const showZoomHelper$ = new BehaviorSubject(false);
  const { xScale$ } = zoomService;
  const minZoom = 10 ** -8; // small enough zoom so as not to leak to page.
  let offsetX = 0;

  function handleZoom(e) {
    const { transform: t, sourceEvent: ev } = e;
    const isPanEvent = ev?.type === "mousemove" || ev?.touches?.length < 2;
    const zs = props$.value.zoomService;

    if (isPanEvent) zs.setMousePosX(ev.clientX - offsetX);

    if (ev) {
      const currentZoom = zs.zoomId$.value;
      const { r2, d2 } = zs.yScale$.value;
      const isZoomFactorSame = t.k === currentZoom.k;
      const isPan = isZoomFactorSame && isPanEvent;
      const maxY = r2 - d2;
      t.y = isPan ? clamp(t.y, maxY, 0) : currentZoom.y;

      zs.setZoomId(t);
    }
  }

  function handleVerticalZoom(e) {
    const zs = props$.value.zoomService;
    const currentZoom = zs.zoomId$.value;
    const { r2, d2 } = zs.yScale$.value;
    const maxY = r2 - d2;
    currentZoom.y = clamp(currentZoom.y - e.deltaY, maxY, 0);
    zs.setZoomId(currentZoom);
    return minZoom;
  }

  function zoomFilter(e) {
    // don't use alt key => goes back on firefox
    const { isGreedy, isActive } = props$.value;
    const isWheel = e.type === "wheel";
    const isVertical = e.shiftKey;
    const isCtrl = e.ctrlKey;

    if (isVertical) return true;
    // prevent zoom explicit
    // prevent zoom on non active greedy.
    if (isWheel && isGreedy && !isActive && !isCtrl) {
      showZoomHelper$.next(true);
      return 0;
    }
    // only allow zoom via ctrl if cooperative
    if (isWheel && !isCtrl && !isGreedy) {
      showZoomHelper$.next(true);
      return 0;
    }

    return true;
  }

  const zoomBehavior = d3Zoom()
    .on("zoom", handleZoom)
    .filter(zoomFilter)
    .wheelDelta((ev) => {
      if (ev.shiftKey) return handleVerticalZoom(ev);
      if (ev.deltaY < minZoom && ev.deltaY > -minZoom) return minZoom;
      return -ev.deltaY * 0.005 * props$.value.zoomTrackingFactor;
    });

  xScale$.pipe(debounceTime(100), takeUntil(destroyed$)).subscribe(() => {
    const zs = props$.value.zoomService;
    const { value: xScale } = zs.xScale$;
    const { maxZoomFactor } = zs;
    const e1 = [xScale.r1, 0];
    const e2 = [xScale.r2, 100000];
    const e3 = [xScale.r2, Infinity];
    zoomBehavior
      .scaleExtent([1, maxZoomFactor])
      .translateExtent([e1, e3])
      .extent([e1, e2]);
  });

  return {
    setZoomRefOffsetX: (x) => (offsetX = x),
    zoomBehavior,
    handleWheel: (e) => {
      const greedy = props$.value.isGreedy;
      const active = props$.value.isActive;
      if (e.shiftKey || e.ctrlKey || (greedy && active)) {
        e.preventDefault();
        e.stopPropagation();
        return null;
      }
      return true;
    },
    showZoomHelper: merge(
      showZoomHelper$,
      showZoomHelper$.pipe(
        debounceTime(400),
        map(() => false)
      )
    ).pipe(distinctUntilChanged()),
  };
}

function ZoomArea({
  children,
  isGreedy,
  zoomBehavior,
  zoomService,
  showZoomHelper,
  handleWheel,
  setZoomRefOffsetX,
}) {
  const zoomHelper = useRef(null);
  const zoomRef = useRef(null);
  const blocker = useRef(null);

  // update zoom
  useEffect(() => {
    const sel = select(zoomRef.current);
    sel.call(zoomBehavior).on("dblclick.zoom", null).on("wheel", handleWheel);
    sel.call(zoomBehavior.transform, zoomService.zoomId);
  }, [zoomBehavior, zoomService, handleWheel]);

  // prevent context menu when grabbing
  useEffect(() => {
    const el = zoomRef.current;
    const preventCt = (e) => e.ctrlKey && e.preventDefault();
    el.addEventListener("contextmenu", preventCt);
    return () => el.removeEventListener("contextmenu", preventCt);
  }, []);

  // show block when processing.
  useEffect(() => {
    const cancelWheelEvent = (e) => e.preventDefault();
    const elem = blocker.current;
    const setBlockVisibility = (isBlocking) => () => {
      const el = blocker.current;
      if (el) el.style.display = isBlocking ? "block" : "none";
    };

    elem.addEventListener("wheel", cancelWheelEvent);

    const sub = zoomService.processes$
      .pipe(
        map((x) => x > 0),
        distinctUntilChanged(),
        tap(setBlockVisibility(true)),
        filter((x) => !x),
        debounceTime(300),
        tap(setBlockVisibility(false))
      )
      .subscribe();

    return () => {
      elem.removeEventListener("wheel", cancelWheelEvent);
      zoomBehavior.on("zoom", null);
      sub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const rect = zoomRef.current.getBoundingClientRect();
    setZoomRefOffsetX(rect.x);
  }, [setZoomRefOffsetX]);

  return (
    <div
      ref={zoomRef}
      style={{ overscrollBehaviorBlock: "block" }}
      className="tw-absolute tw-inset-0 tw-overflow-hidden"
    >
      {children}
      <div
        ref={blocker}
        className="tw-absolute tw-inset-0 tw-top-0 tw-cursor-progress"
      />
      <div
        ref={zoomHelper}
        style={{ opacity: showZoomHelper ? "100%" : "0%" }}
        className="tw-absolute tw-flex tw-items-center tw-justify-center tw-inset-0 tw-pointer-events-none tw-transition-opacity tw-duration-1000"
      >
        <div className="tw-absolute tw-inset-0 bg-1 tw-opacity-70" />
        <div className="color-2 tw-z-10 tw-text-xl">
          {isGreedy ? (
            <div>
              <div className="tw-text-xl tw-text-center">
                click to activate zoom by scrolling
              </div>
              <div className="tw-text-sm tw-text-center">
                Press <b>SHIFT</b> key while scrolling to pan vertically
              </div>

              <div className="tw-text-sm tw-text-center tw-opacity-80 tw-mt-6">
                You can change this behavior in settings
              </div>
            </div>
          ) : (
            <div>
              <div className="tw-text-xl tw-text-center">
                Press CTRL while scrolling to zoom
              </div>
              <div className="tw-text-sm tw-text-center">
                Press <b>SHIFT</b> while scrolling to pan vertically
              </div>
              <div className="tw-text-sm tw-text-center tw-opacity-80 tw-mt-6">
                You can change this behavior in settings
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

ZoomArea.propTypes = {
  handleWheel: propTypes.func.isRequired,
  children: propTypes.node.isRequired,
  isGreedy: propTypes.bool.isRequired,
  zoomBehavior: propTypes.oneOfType([
    propTypes.func,
    propTypes.shape({
      transform: propTypes.shape({}).isRequired,
      zoomId: propTypes.shape({}).isRequired,
    }),
  ]).isRequired,
  zoomService: propTypes.instanceOf(ZoomService).isRequired,
  showZoomHelper: propTypes.bool.isRequired,
  setZoomRefOffsetX: propTypes.func.isRequired,
};

export default xstream(controller)(ZoomArea);
